import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} bubble
 * @property {HTMLVideoElement} bubbleVideo
 * @property {HTMLVideoElement} modalVideo
 *
 * A floating, auto-playing (muted) product video bubble. Clicking it opens
 * the same video larger, with sound, in a modal (inherited from
 * DialogComponent). Closing the bubble's own close button removes it and
 * remembers the dismissal for the rest of the day, per product.
 *
 * @extends {DialogComponent<Refs>}
 */
class ProductVideoPopupComponent extends DialogComponent {
  requiredRefs = ['dialog', 'bubble', 'bubbleVideo', 'modalVideo'];

  connectedCallback() {
    super.connectedCallback();

    if (this.#isDismissedToday()) {
      this.remove();
      return;
    }

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Opens the modal. Overrides DialogComponent to swap the muted bubble
   * loop for the same video, unmuted, picking up where the bubble left off.
   * The unmuted .play() call happens synchronously in the click handler so
   * the browser treats it as user-initiated and allows sound.
   */
  showDialog() {
    const { bubbleVideo, modalVideo } = this.refs;

    modalVideo.currentTime = bubbleVideo.currentTime || 0;
    bubbleVideo.pause();

    super.showDialog();

    modalVideo.muted = false;
    modalVideo.play().catch(() => {});
  }

  #handleDialogClose = () => {
    const { bubbleVideo, modalVideo } = this.refs;

    modalVideo.pause();
    bubbleVideo.play().catch(() => {});
  };

  /**
   * Dismisses the bubble for the rest of the day (per product).
   */
  dismiss = () => {
    this.#setDismissedToday();
    this.remove();
  };

  #todayKey() {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
  }

  #isDismissedToday() {
    try {
      return localStorage.getItem(this.dataset.storageKey ?? '') === this.#todayKey();
    } catch {
      return false;
    }
  }

  #setDismissedToday() {
    try {
      if (this.dataset.storageKey) localStorage.setItem(this.dataset.storageKey, this.#todayKey());
    } catch {
      // Storage may be unavailable (e.g. private browsing) - dismissal just won't persist.
    }
  }
}

if (!customElements.get('product-video-popup')) {
  customElements.define('product-video-popup', ProductVideoPopupComponent);
}
